import React from 'react';
import { List, Map } from 'immutable';

import { ImmutableMap, Optional, Result, RenderedMessage } from './utils';
import * as controls from './controls';

export type Direction = 'up' | 'down' | 'left' | 'right';
// not immutable
export type Location = ImmutableMap<{x: number, y: number}>;
export type Player = ImmutableMap<{type: 'player', id: number, facing: Optional<Direction>}>;
export type Rock = ImmutableMap<{type: 'rock'}>;
// difference from rock is that player can be interposed with shiny
export type Shiny = ImmutableMap<{type: 'shiny'}>;
export type Wall = ImmutableMap<{type: 'wall'}>;
export type Enter = ImmutableMap<{type: 'enter'}>;
export type Exit = ImmutableMap<{type: 'exit'}>;
export type WormholeIn = ImmutableMap<{type: 'wormholeIn', id: number}>;
// TODO store offset here, use in equivalent_item also
export type WormholeOut = ImmutableMap<{
  type: 'wormholeOut', id: number, out_id: number, pushable: boolean,
  default_direction: Optional<Direction>,
}>;
export type Key = ImmutableMap<{type: 'key', facing: Direction}>;
export type Door = ImmutableMap<{type: 'door'}>;
export type HatchingEgg = ImmutableMap<{type: 'hatching_egg'}>;
export type Egg = ImmutableMap<{type: 'egg'}>;
export type Eggshell = ImmutableMap<{type: 'egg', flipped: boolean}>;

// export type Button = ImmutableMap<{type: 'button', id: number}>;
// export type Gate = ImmutableMap<{type: 'gate', id: number}>;

export type Item = Player | Rock | Wall | Exit | WormholeIn | WormholeOut | Shiny | Egg | Eggshell;
export type ItemType =
  'player' | 'rock' | 'wall'
  | 'enter' | 'exit' | 'wormholeIn' | 'wormholeOut'
  | 'hatching_egg' | 'egg' | 'eggshell' | 'shiny' | 'key' | 'door';

export function getItemType(item: Item): ItemType {
  return (item as any).get('type');
}

export function equivalent_item(item1: Item, item2: Item): boolean {
  if (getItemType(item1) !== getItemType(item2)) {
    return false;
  }
  let t = getItemType(item1);
  if (t === 'wormholeIn') {
    return (item1 as WormholeIn).get('id') === (item2 as WormholeIn).get('id');
  }
  if (t === 'wormholeOut') {
    if ((item1 as WormholeOut).get('id') !== (item2 as WormholeOut).get('id')) {
      return false;
    }
    if ((item1 as WormholeOut).get('out_id') !== (item2 as WormholeOut).get('out_id')) {
      return false;
    }
    return true;
  }
  return true;
}

export function pushable(item: Item, unpushable?: List<Item>): boolean {
  let unpushable_list = unpushable || List<Item>();
  for (let i = 0; i < unpushable_list.size; i++) {
    if (equivalent_item(unpushable_list.get(i) as Item, item)) {
      return false;
    }
  }
  let t = getItemType(item)
  if (['shiny', 'rock', 'player', 'key', 'egg'].includes(t)) {
    return true;
  }
  if (t === 'wormholeOut') {
    return (item as WormholeOut).get('pushable');
  }
  return false;
}

// assumes item is already pushable
export function overlayable(item: Item): boolean {
  let t = getItemType(item)
  if (t === 'wormholeOut') {
    return true;
  }
  return false;
}

export type WormholeOutState = ImmutableMap<{
  out_id: number,
  base_t: number, // time the out starts existing

  // time from wormhole's point of view, at time of existence.
  // if borrowed, is unknown until debt is resolved
  // always known if there are only positive wormholes
  start_age: Optional<number>,

  delta_t: number, // offset from the in, enforce that these are unique
  location: Optional<Location>,
  nested_in: Optional<number>, // might be within another wormhole
  nested_age: Optional<number>, // age at time of entering wormhole
}>;

export type WormholeState = ImmutableMap<{
  in: Location,
  // list of outs, lowest start_age is the active one.  (at least one guaranteed to not be null)
  outs: List<WormholeOutState>,
  outPushable: boolean,
  singularity: boolean,
}>;


export function display_wormhole_in(color: string, delta: number, extra_css?: React.CSSProperties) {
  let text;
  if (delta === Infinity) {
    text = '+∞';
  } else if (delta > 0) {
    text = `+${delta}`
  } else if (delta < 0) {
    text = `-${-delta}`
  } else {
    text = ''
  }
  return <span className="wormholeIn" style={{
    backgroundColor: color,
    display: 'inline-block',
    ...(extra_css || {}),
  }}><span className="text">{text}</span></span>;
}

export function display_wormhole_out(color: string, delta: number, pushable: boolean, extra_css?: React.CSSProperties) {
  let text;
  if (delta < 0) {
    // in proper game, negative times should be associated with outs.  but we don't need that here
    // text = `${delta}`
    text = ''
  } else {
    text = ''
  }
  return <span className={"wormholeOut" + (pushable ? ' pushable' : '')} style={{
    borderColor: color,
    display: 'inline-block',
    // borderColor: 'white',
    ...(extra_css || {}),
  }}>{text}</span>;
}

export const wormhole_colors = [
  '#d9cb5f', // '#dddd44',
  '#aa44aa',
  // unused
  '#ffff44',
  '#ff44ff',
  '#00ffff',
  '#ff0000',
  '#0000ff',
  '#00ff00',
  '#ffff88',
  '#ff88ff',
  '#88ffff',
  '#ff8888',
  '#8888ff',
  '#88ff88',
];

function drawKeySvg() {
  /* https://fontawesomeicons.com/svg/icons/key */
  return <svg width="100%" height="100%" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <path d={
      "M0 8a4 4 0 0 1 7.465-2H14a.5.5 0 0 1 .354.146l1.5 1.5a.5.5 0 0 1 0 .708l-1.5 1.5a.5.5 0 0 1-.708 0L13 9.207l-.646.647a.5.5 0 0 1-.708 0L11 9.207l-.646.647a.5.5 0 0 1-.708 0L9 9.207l-.646.647A.5.5 0 0 1 8 10h-.535A4 4 0 0 1 0 8zm4-3a3 3 0 1 0 2.712 4.285A.5.5 0 0 1 7.163 9h.63l.853-.854a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.646-.647a.5.5 0 0 1 .708 0l.646.647.793-.793-1-1h-6.63a.5.5 0 0 1-.451-.285A3 3 0 0 0 4 5z"
    } stroke="#ffff00" fill={"#ffff00"} strokeWidth={1}/>
    <path
      d="M4 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"
      stroke="black" fill={"black"}/>
  </svg>
}

function drawDoorSvg() {
  /* https://fontawesomeicons.com/svg/icons/shield-lock */
  return <svg width="100%" height="100%" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
    <path d={
      "M9.5 6.5a1.5 1.5 0 0 1-1 1.415l.385 1.99a.5.5 0 0 1-.491.595h-.788a.5.5 0 0 1-.49-.595l.384-1.99a1.5 1.5 0 1 1 2-1.415z"
    } stroke="black" fill={"black"}/>
  </svg>
}


type ItemDisplayOptions = {
  inactive?: boolean,
  inline?: boolean,
  key?: string | number,
};
export function item_display(state: GameState, item: Item, display_opts: ItemDisplayOptions): RenderedMessage {
  if (display_opts.inline) {
    return <div style={{display: 'inline-block', height: '1em', width: '1em'}} key={display_opts.key}>
      {item_display(state, item, {...display_opts, inline: false, key: undefined})}
    </div>;
  }
  // TODO: display wormholes better?
  switch (getItemType(item)) {
    case 'player': {
      let deg: number;
      let img = 'bird-top'
      switch ((item as Player).get('facing')) {
        case 'right':
          deg = 90;
          break;
        case 'left':
          deg = 270;
          break;
        case 'up':
          deg = 0;
          break;
        case 'down':
          deg = 180;
          break;
        default:
          img = 'bird-right';
          deg = 0;
          break;
          // throw new Error(`Unknown facing: ${(item as Player).get('facing')}`);
      }
      let src = `${process.env.PUBLIC_URL + `/img/${img}.png`}`;
      return <img key={display_opts.key} className="player" alt="player" src={src} style={{
          // rotated image
          transform: 'rotate(' + deg + 'deg)',
          opacity: display_opts.inactive ? 0.6 : 1,
      }}/>
    }
    case 'rock':
      // return <div key={display_opts.key} className="rock" style={{}}/>
      return <div key={display_opts.key} className="item" style={{
          backgroundImage: `url(${process.env.PUBLIC_URL + '/img/rock.png'})`,
          width: '84%', height: '84%', margin: '8%',
      }}/>
    case 'wall':
      return <div key={display_opts.key} className="wall" style={{}}/>
    case 'exit':
      return <div key={display_opts.key} className="exit" style={{}}/>
    case 'enter':
      return <div key={display_opts.key} className="enter" style={{}}/>
    case 'wormholeIn': {
      let id = (item as WormholeIn).get('id');
      let delta_t = best_guess_delta_for_wormhole_in(state, id);
      return <React.Fragment key={display_opts.key}>
        {display_wormhole_in(
          wormhole_colors[(item as WormholeIn).get('id')],
          delta_t,
        )}
      </React.Fragment>
    }
    case 'wormholeOut': {
      let out = item as WormholeOut;
      let delta_t = state.getIn(['wormholes', out.get('id'), 'outs', out.get('out_id'), 'delta_t']) as number;
      let pushable = out.get('pushable');
      return <React.Fragment key={display_opts.key}>
        {display_wormhole_out(
          wormhole_colors[(item as WormholeOut).get('id')],
          delta_t,
          pushable,
        )}
      </React.Fragment>
    }
    case 'hatching_egg': {
      let img_url = 'hatch5'
      state.get('events').forEach((event: Event) => {
        if (getEventType(event) === 'hatch')  {
          let hatching = (event as HatchEvent).get('time') - getTime(state) + 1;
          if (hatching <= 4) {
            img_url = `hatch${5-hatching}`
          } else {
            img_url = `egg`
          }
        }
      });
      return <div key={display_opts.key} className="item egg" style={{
          backgroundImage: `url(${process.env.PUBLIC_URL + `/img/${img_url}.png`})`,
      }}/>
    }
    case 'egg':  {
      let img_url = 'egg'
      return <div key={display_opts.key} className="item egg" style={{
          backgroundImage: `url(${process.env.PUBLIC_URL + `/img/${img_url}.png`})`,
      }}/>
    }
    case 'eggshell':  {
      return <div key={display_opts.key} className="item eggshell" style={{
          backgroundImage: `url(${process.env.PUBLIC_URL + '/img/eggshell.png'})`,
          transform: (item as Eggshell).get('flipped') ? 'scaleX(-1)' : '',
      }}/>
    }
    case 'shiny': {
      /* return <div key={display_opts.key} className="shiny" style={{}}/> */
      return <div key={display_opts.key} className="item shiny" style={{
          backgroundImage: `url(${process.env.PUBLIC_URL + '/img/shiny.png'})`,
          width: '68%', height: '68%', margin: '16%',
      }}/>
      // return <div key={display_opts.key} className="shiny" style={{}}>
      //     <img src={process.env.PUBLIC_URL + '/img/bird-right-body.png'} alt="bird" style={{position: 'absolute', height: '0.6em', marginLeft: '-0.00em', marginTop: '0.3em'}}/>
      // </div>
    }
    case 'door': {
      return <div key={display_opts.key} className="door" style={{}}>
          {drawDoorSvg()}
        </div>
    }
    case 'key': {
      return <div key={display_opts.key} className="key" style={{}}>
        {drawKeySvg()}
      </div>
    }
        /*
    case 'twig':
      children.push(
        <div key={i} className="twig" style={{}}/>
      );
      break;
         */
    default:
      throw new Error(`Unknown item type: ${getItemType(item)}`);
  }
}

export function items_display(state: GameState, items: List<Item>, display_opts: ItemDisplayOptions): RenderedMessage {
  return <div className="items" style={{display: display_opts.inline ? 'inline-block': 'block'}}>
      {items.map((item, i) => {
      return item_display(state, item, {...display_opts, key: i});
      }).toArray()}
  </div>
}

export function find_positive_out(state: GameState, id: number): Optional<WormholeOutState> {
  let wormhole_state = state.get('wormholes').get(id);
  if (wormhole_state === undefined) {
    throw new Error(`no wormhole ${id}`);
  }
  // for now just assume there's at most one
  let outs = wormhole_state.get('outs').filter((out) => out.get('delta_t') >= 0);
  if (outs.size === 0) {
    return null;
  }
  if (outs.size > 1) {
    throw new Error(`multiple positive outs for wormhole ${id}`);
  }
  return outs.first();
}

export function best_guess_delta_for_wormhole_in(state: GameState, id: number): number {
  // console.log(`best_guess_delta_for_wormhole_in ${id}`);
  let wormhole_state = state.get('wormholes').get(id);
  if (wormhole_state === undefined) {
    throw new Error(`no wormhole ${id}`);
  }
  if (wormhole_state.get('singularity')) {
    return Infinity;
  }
  // let out = find_positive_out(state, id);
  // if (out === null) {
  let out = active_wormhole_out(wormhole_state);
  // }
  let delta = 0;
  let within_id = out.get('nested_in');
  if (within_id !== null) {
    // console.log(`wormhole ${id} is within wormhole ${within_id}`);
    delta += best_guess_delta_for_wormhole_in(state, within_id);
  }
  // console.log(`best_guess_delta_for_wormhole_in ${id} ${delta} ${out.get('delta_t')}`);
  return delta + out.get('delta_t');
}

// needed only for forward time travel
export function active_wormhole_out(wormhole: WormholeState): WormholeOutState {
  return (
    wormhole.get('outs')
    .filter(out => ((out.get('location') !== null) || (out.get('nested_in') !== null)))
    .filter(out => out.get('start_age') !== null)
    .get(0) // there should be exactly one
  ) as WormholeOutState;
}

export function wormhole_out_age(state: GameState, wormhole_out: WormholeOutState): Optional<number> {
  if (wormhole_out.get('nested_in') !== null) { return wormhole_out.get('nested_age'); }
  let start_age = wormhole_out.get('start_age');
  if (start_age === null) {return null;}
  return start_age + getTime(state) - wormhole_out.get('base_t');
}

export type Cell = ImmutableMap<{
  items: List<Item>,
}>;
export type Grid = List<List<Cell>>;

export type EnterWormholeEvent = ImmutableMap<{
  type: 'enter_wormhole',
  wormhole_id: number,
  time: number,
  // todo: make it possible to summon multiple items in the same timestep?
  item: Item,
  direction: Direction | null,
}>;
export type ExitWormholeEvent = ImmutableMap<{
  type: 'exit_wormhole',
  wormhole_id: number,
  wormhole_age: number,
  items: List<Item>,
  direction: Direction,
  enter_time: number,
}>;
export type ExitEvent = ImmutableMap<{
  type: 'exit',
  item: Item,
  // TODO: track wormholes by ID
  time: Optional<number>,
}>;
export type LayEvent = ImmutableMap<{
  type: 'lay',
  player_id: number,
  age: number,
}>;
export type HatchEvent = ImmutableMap<{
  type: 'hatch',
  player_id: number,
  time: number, // conceptually should be based on player age, oh well
}>;
export type Event = EnterWormholeEvent | ExitWormholeEvent | ExitEvent | LayEvent | HatchEvent; 
export type EventType = 'enter_wormhole' | 'exit_wormhole' | 'exit' | 'lay' | 'hatch';
export function getEventType(event: Event): EventType {
  return (event as any).get('type');
}

export function estimate_exit_wormhole_remaining_time(state: GameState, ex_ev: ExitWormholeEvent) {
  let wormhole_state = state.get('wormholes').get(ex_ev.get('wormhole_id'));
  if (wormhole_state === null || wormhole_state === undefined) {
    throw new Error('wormhole not found');
  }
  let age = wormhole_state.get('outs').map(out => {
    return wormhole_out_age(state, out);
  }).filter(age => age !== null).get(0);
  if (age === null || age === undefined) {
    throw new Error('active wormhole out not found');
  }
  return ex_ev.get('wormhole_age') - age + 1;
}

export type PlayerState = ImmutableMap<{
  location: Location | null,
  moved: boolean,
  age: number,
}>

export type GameState = ImmutableMap<{
  grid: Grid,
  past: List<GameState>,
  // for each player, a list of indices into the history of game states
  players: Map<number, PlayerState>,
  wormholes: Map<number, WormholeState>,
  created: boolean, // to know whether to give better error message
  events: List<Event>,
  parallel_time: number,
}>;

let USE_PARALLEL = false; // NOTE: this is probably broken now
export function existingPlayers(state: GameState): List<number> {
  return state.get('players').filter(p => p.get('location') !== null).keySeq().toList();
}

function getNextPlayerId(state: GameState): number {
  let keys = state.get('players').keySeq().toSet();
  let next_id = 0;
  while (keys.has(next_id)) {
    next_id++;
  }
  return next_id;
}

export function getTime(state: GameState): number {
  if (USE_PARALLEL) {
    return state.get('parallel_time');
  }
  return state.get('past').size;
}
export function isWon(state: GameState): boolean {
   return state.get('events').size === 0
}
// export type GameState = List<>;

export function getCell(grid: Grid, loc: Location): Cell {
  return grid.getIn([loc.get('y'), loc.get('x')]) as Cell;
}
export function updateGrid(grid: Grid, loc: Location, f: (cell: Cell) => Cell): Grid {
  return grid.updateIn([loc.get('y'), loc.get('x')], f as any);
}
export function setGrid(state: GameState, loc: Location, f: (cell: Cell) => Cell): GameState {
  return state.update('grid', (grid) => updateGrid(grid, loc, f));
}
export function updateCell(cell: Cell, f: (items: List<Item>) => List<Item>): Cell {
  return cell.update('items', (f as any));
}
export function cellUpdater(f: (items: List<Item>) => List<Item>): (cell: Cell) => Cell {
  return (cell: Cell) => updateCell(cell, f);
}

export function findCell(grid: Grid, predicate: (cell: Cell) => boolean): Optional<Location> {
  for (let y = 0; y < grid.size; y++) {
    for (let x = 0; x < (grid.get(y) as List<Cell>).size; x++) {
      const cell = grid.getIn([y, x]) as Cell;
      if (predicate(cell)) {
        return Map({ x, y }) as Location;
      }
    }
  }
  return null;
}

// enter wormhole =
//    add grid state with removed player
//    clone histories

export type MoveAction = ImmutableMap<{
  type: 'move',
  playerId: number,
  direction: Direction,
}>;
export type WaitAction = ImmutableMap<{
  type: 'wait',
}>;
export type EnterAction = ImmutableMap<{
  type: 'enter',
  item: Item,
  wormhole_id: number,
  wormhole_out_id: number,
  direction: Direction | null,
  display_direction: Direction | null, // only relevant when direction is null
}>;

export type Action = MoveAction | WaitAction | EnterAction;
export type ActionType = 'move' | 'wait' | 'enter';

export function getActionType(action: Action): ActionType {
  return (action as any).get('type');
}


export function moveLocation(grid: Grid, location: Location, direction: Direction): Result<Optional<Location>> {
  let new_location;
  switch (direction) {
    case 'up':
      new_location = location.update('y', (y: number) => y - 1);
      if (new_location.get('y') < 0) {return [null, null]}
      break;
    case 'down':
      new_location = location.update('y', (y: number) => y + 1);
      if (new_location.get('y') > grid.size - 1) {return [null, null]}
      break;
    case 'left':
      new_location = location.update('x', (x: number) => x - 1);
      if (new_location.get('x') < 0) {return [null, null]}
      break;
    case 'right':
      new_location = location.update('x', (x: number) => x + 1);
      if (new_location.get('x') > (grid.get(0) as any).size - 1) {return [null, null]}
      break;
    default:
      return [null, `Unknown direction ${direction}`];
  }
  return [new_location, null];
}

export function maybeUpdatePushedItemLocation(state: GameState, pushed_item: Item, new_location: Optional<Location>): GameState {
  if (getItemType(pushed_item) === 'player') {
    let playerId = (pushed_item as Player).get('id');
    state = state.setIn(['players', playerId, 'location'], new_location)
  }
  if (getItemType(pushed_item) === 'wormholeOut') {
    let wormholeId = (pushed_item as WormholeOut).get('id');
    let wormholeOutId = (pushed_item as WormholeOut).get('out_id');
    state = state.setIn(['wormholes', wormholeId, 'outs', wormholeOutId, 'location'], new_location)
  }
  return state;
}

export function maybeUpdatePushedItemsLocations(state: GameState, pushed_items: List<Item>, new_location: Optional<Location>): GameState {
  for (let pushed_item of pushed_items) {
    state = maybeUpdatePushedItemLocation(state, pushed_item, new_location);
  }
  return state;
}

export function exitWormhole(state: GameState, pushed_items: List<Item>, wormhole_id: number, wormhole_out_id: number, direction: Direction): Result<Optional<GameState>> {
  let wormhole: WormholeState | null = state.getIn(['wormholes', wormhole_id]) as WormholeState;
  if (!wormhole) {return [null, `Wormhole was missing at time ${getTime(state)}`]}
  let wormhole_out = wormhole.get('outs').find(out => out.get('out_id') === wormhole_out_id);
  if (wormhole_out === undefined) {
    throw new Error(`Wormhole out was missing, could not exit?`)
  }
  let new_location = wormhole_out.get('location');
  if (new_location === null) {
    throw new Error(`Wormhole out has no location, could not exit?`)
  }
  let [push_state, err] = tryPush(
    state, {
      pushed_items, new_location, direction,
      unpushable: List([Map({type: 'wormholeOut', id: wormhole_id, out_id: wormhole_out_id})]),
    });
  if (push_state === null) {
    if (err) {
      return [null, err];
    }
    return [null, `Wormhole exit blocked`];
  }
  let new_state = push_state;
  new_state = maybeUpdatePushedItemsLocations(new_state, pushed_items, new_location);
  for (let pushed_item of pushed_items) {
    if (getItemType(pushed_item) === 'player') {
      let playerId = (pushed_item as Player).get('id');
      new_state = new_state.setIn(['players', playerId, 'moved'], true)
    }
  }
  return [new_state, null]
}

export function tryWormhole(state: GameState, pushed_items: List<Item>, wormhole_id: number, direction: Direction): Result<Optional<GameState>> {
  let new_state: GameState;
  let wormhole: WormholeState | null = state.getIn(['wormholes', wormhole_id]) as WormholeState;
  if (!wormhole) {return [null, `No wormhole with id ${wormhole_id}`]}
  if (wormhole.get('singularity')) {
    new_state = maybeUpdatePushedItemsLocations(state, pushed_items, null);
    for (let pushed_item of pushed_items) {
      if (getItemType(pushed_item) === 'wormholeOut') {
        let pushed_wh = pushed_item as WormholeOut;
        new_state = new_state.setIn(['wormholes', pushed_wh.get('id'), 'singularity'], true)
        new_state = new_state.setIn(['wormholes', pushed_wh.get('id'), 'outs', 'delta_t'], Infinity)
      }
    }
    return [new_state, null];
  }
  let wormhole_out = active_wormhole_out(wormhole);
  let dt = wormhole_out.get('delta_t');
  if (dt < 0) {
    if (pushed_items.size > 1) {
      return [null, <React.Fragment>{items_display(state, pushed_items, {})}{" "}do not exit a wormhole</React.Fragment>];
    }
    let pushed_item = pushed_items.get(0) as Item;
    let wrong_time = false;
    let wrong_dir = false;
    let event = state.get('events').find((event: Event) => {
      if (getEventType(event) !== 'enter_wormhole')  {
        return false;
      }
      let enter_wormhole_event = event as EnterWormholeEvent;
      if (wormhole_id !== enter_wormhole_event.get('wormhole_id')) {
        return false;
      }
      if (getItemType(pushed_item) === 'wormholeOut') {
        if (!equivalent_item(
          // TODO for now, always assume we borrow one id higher
          pushed_item.update('out_id', (id) => (id+1)),
          enter_wormhole_event.get('item'))
        ) {
          return false;
        }
        // TODO maybe a friendly error message if type + wormhole ID match but out ID doesnt
      } else {
        if (!equivalent_item(pushed_item, enter_wormhole_event.get('item'))) {
          return false;
        }
      }
      if (getTime(state) !== enter_wormhole_event.get('time')) {
        wrong_time = true;
        return false;
      }
      let enter_direction = enter_wormhole_event.get('direction');
      if (enter_direction !== null) {
        if (direction !== enter_direction) {
          wrong_dir = true;
          return false;
        }
      }
      return true;
    });
    if (event === undefined) {
      // let msg = <React.Fragment>{item_display(state, pushed_item, {inline: true})} {" "} does not enter a wormhole at time {getTime(state)}</React.Fragment>;
      let exit_wormhole = Map({
        type: 'wormholeOut', id: wormhole_id, out_id: wormhole_out.get('out_id'), pushable: wormhole.get('outPushable')
      }) as WormholeOut
      let msg = <React.Fragment>
        {item_display(state, pushed_item, {inline: true})} {" does not enter "}
        {item_display(state, exit_wormhole, {inline: true})} {" "}
        {wrong_dir ? <React.Fragment>going <kbd>{direction}</kbd> {" "}</React.Fragment> : null}
        at this time.  
      </React.Fragment>;
      if (!state.get('created')) {
        msg = <React.Fragment>{msg} {" "} <br/><kbd>{controls.borrow.main}</kbd> creates items from the future.</React.Fragment>;
      }
      if (wrong_time) {
        msg = <React.Fragment>{msg} {" "} <br/> Use <kbd>{controls.wait.main}</kbd> if you need to stall.</React.Fragment>;
      }
      return [null, msg];
    } else {
      new_state = removeEvent(state, event);
      // TODO refactor this into a function?
      if (getItemType(pushed_item) === 'player') {
        let playerId = (pushed_item as Player).get('id');
        new_state = new_state.removeIn(['players', playerId])
      }
      if (getItemType(pushed_item) === 'wormholeOut') {
        let wormholeId = (pushed_item as WormholeOut).get('id');
        let outId = (pushed_item as WormholeOut).get('out_id');
        new_state = new_state.setIn(['wormholes', wormholeId, 'outs', outId, 'location'], null)
      }
      return [new_state, null];
    }
  } else { // (dt >= 0)
    let remaining_pushed_items: List<Item> = List();
    new_state = state;
    let unfolded = false;
    let in_location = wormhole.get('in')
    for (let pushed_item of pushed_items) {
      if (getItemType(pushed_item) === 'wormholeOut') {
        let wormhole_out_pushed = pushed_item as WormholeOut;
        if (wormhole_out_pushed.get('id') === wormhole_id) {
          if (wormhole.get('outs').size > 1) {
            return [null, `Cannot unfold spacetime with other wormhole exits`];
          }
          // new_state = new_state.setIn(['wormholes', wormhole_id, 'outs', wormhole_out.get('out_id'), 'location'], null);
          new_state = new_state.setIn(['wormholes', wormhole_id, 'outs'], List());
          if (dt === 0) {
            // spacetime unfolds
            // remove the in from the grid
            // there should be only one item
            new_state = setGrid(new_state, in_location, cellUpdater(items => List()));
            unfolded = true;
            // TODO: ensure there are no other wormhole outs lying around?
            // TODO: also completely remove portal state?
          } else {
            new_state = new_state.setIn(['wormholes', wormhole_id, 'singularity'], true);
            new_state = maybeUpdatePushedItemsLocations(new_state, pushed_items, null);
            return [new_state, null]
          }
        } else {
          // push a wormhole out through a different wormhole
          remaining_pushed_items = remaining_pushed_items.push(wormhole_out_pushed);
          let wormhole_out_pushed_state = new_state.getIn(['wormholes', wormhole_out_pushed.get('id'), 'outs', wormhole_out_pushed.get('out_id')]) as WormholeOutState;
          new_state = new_state.setIn([
            'wormholes', wormhole_out_pushed.get('id'), 'outs', wormhole_out_pushed.get('out_id'), 'nested_age'], wormhole_out_age(new_state, wormhole_out_pushed_state)
          );
          new_state = new_state.setIn([
            'wormholes', wormhole_out_pushed.get('id'), 'outs', wormhole_out_pushed.get('out_id'), 'nested_in'], wormhole_id
          );
        }
      } else {
        remaining_pushed_items = remaining_pushed_items.push(pushed_item);
      }
    }
    if (unfolded) {
      new_state = setGrid(new_state, in_location, cellUpdater(items => remaining_pushed_items));
      return [new_state, null];
    }
    // assert start_age is known
    const age = wormhole_out_age(new_state, wormhole_out);
    if (age === null) {
      throw new Error(`TODO: Wormhole ${wormhole_id} has no known age`);
    }
    // item simply goes away.
    new_state = new_state.update('events', (events: List<Event>) => events.push(
      Map({
        type: 'exit_wormhole',
        wormhole_id: wormhole_id,
        // exit is in wormhole's time
        wormhole_age:  age + dt,
        items: remaining_pushed_items,
        direction: direction,
        enter_time: getTime(new_state),
      }) as ExitWormholeEvent
    ));
    new_state = maybeUpdatePushedItemsLocations(new_state, remaining_pushed_items, null);
    return [new_state, null];
  }
}

export function removeEvent(state: GameState, event: Event): GameState {
  let new_state = state.set('events', state.get('events').filter(e => e !== event));
  if (new_state.get('events').size !== state.get('events').size - 1) {
    throw new Error(`Strange, removed multiple events`);
  }
  return new_state;
}

type TryPushOptions = {
  pushed_items: List<Item>,
  new_location: Location,
  direction: Direction,
  unpushable?: List<Item>,
}
export function tryPush(state: GameState, options: TryPushOptions): Result<Optional<GameState>> {
  let {pushed_items, new_location, direction} = options;

  let grid = state.get('grid');
  let new_state: GameState = state;
  console.log(`pushing ${pushed_items.toJS()} ${direction} to ${new_location.get('x')},${new_location.get('y')}`);
  const cell = getCell(grid, new_location);
  const items = cell.get('items');
  let has_wormhole = items.filter((item: Item) => getItemType(item) === 'wormholeIn').size;
  let has_exit = items.filter((item: Item) => getItemType(item) === 'exit').size;
  if (has_wormhole) {
    if (items.size > 1) {
      throw new Error(`Wormhole In has multiple items ${items}`);
    }
    let wormhole_id: number = (items.first() as WormholeIn).get('id');
    console.log(`Pushed ${items_display(state, pushed_items, {})} ${direction} through wormhole ${wormhole_id}`);
    return tryWormhole(state, pushed_items, wormhole_id, direction);
  } else if (has_exit) {
    for (let pushed_item of pushed_items) {
      let wrong_time = false;
      let event = state.get('events').find((event: Event) => {
        if (getEventType(event) !== 'exit')  {
          return false;
        }
        let exit_event = event as ExitEvent;
        let item_t = exit_event.get('item');
        if (!equivalent_item(item_t, pushed_item)) {
          return false;
        }
        if (exit_event.get('time') && exit_event.get('time') !== getTime(state)) {
          wrong_time = true;
          return false;
        }
        return true;
      });
      if (event === undefined) {
        let exit_item = Map({type: 'exit'});
        let time_str: string | React.ReactElement = "";
        if (wrong_time) {
          time_str = <React.Fragment>
            {" at this time.  Use "}
            <kbd>{controls.wait.main}</kbd>
            {" to wait."}
          </React.Fragment>
        }
        return [
          null, 
          <React.Fragment>
            {item_display(new_state, pushed_item, {inline: true})} {" does not enter "}
            {item_display(new_state, exit_item, {inline: true})} {time_str}
          </React.Fragment>
        ]; 
      } else {
        new_state = removeEvent(new_state, event);
      }
    }
    new_state = maybeUpdatePushedItemsLocations(new_state, pushed_items, null);
    return [new_state, null];
  } else {
    let blocked = items.filter((item: Item) => ['wall'].includes(getItemType(item))).size;
    if (blocked) {
      return [null, null]; // new Error(`Could not push ${direction} ${items}`)];
    }
    let blocked_door = items.filter((item: Item) => ['door'].includes(getItemType(item))).size;
    if (blocked_door) {
      if (pushed_items.find(item => getItemType(item) === 'key') === undefined) {
        return [null, null]; // new Error(`Could not push ${direction} ${items}`)];
      }
      new_state = setGrid(new_state, new_location, cellUpdater(items => pushed_items.filter(item => getItemType(item) !== 'key')));
      return [new_state, null];
    }
    let new_pushed_items = items.filter((item: Item) => pushable(item, options.unpushable));
    let unpushed_items = items.filter((item: Item) => !pushable(item, options.unpushable));
    if (new_pushed_items.size) {
        let [newer_location, move_err] = moveLocation(state.get('grid'), new_location, direction);
        if (newer_location === null) {
          return [null, move_err];
        }
        let [maybe_new_state, maybe_err] = tryPush(new_state, {pushed_items: new_pushed_items, new_location: newer_location, direction});
        if (maybe_err) {return [null, maybe_err]}
        if (maybe_new_state === null) {
          if (new_pushed_items.some((item: Item) => !overlayable(item))) {
            return [
              null, null // new Error(`${itemType} could not be pushed ${direction}`)
            ]
          } else {
            // all overlayable, don't push, but don't error
            unpushed_items = items;
            new_pushed_items = List<Item>();
          }
        } else {
          new_state = maybe_new_state;
        }
        // TODO: make sure items are still in the same location?
        // alternatively, remove the items from the grid before tryPush.  this allows "cyclical" pushing
        // could make a level based on that
        new_state = setGrid(new_state, new_location, cellUpdater(items => unpushed_items));
    }

    new_state = setGrid(new_state, new_location, cellUpdater(items => items.concat(pushed_items)));
    new_state = maybeUpdatePushedItemsLocations(new_state, pushed_items, new_location);
  }

  return [new_state, null];
}

export function tryMove(state: GameState, action: MoveAction): Result<Optional<GameState>> {
  let playerId = action.get('playerId');
  let player: PlayerState | undefined = state.getIn(['players', playerId]) as PlayerState | undefined;
  if (player === undefined) {
    if (!state.get('players').size) {
      return [null, <React.Fragment>No players found!  Use <kbd>{controls.wait.main}</kbd> to wait.</React.Fragment>];
    }
    return [null, `Player ${playerId} not found.  x to switch`];
  }

  let moved = state.getIn(['players', playerId, 'moved']);
  if (moved) {
    state = state.update('parallel_time', time => time + 1);
    state = state.update('players', players => players.map((player_s: PlayerState) =>
      player_s.set('moved', false)
    ));
    state = state.updateIn(['players', playerId, 'moved'], () => true);
  } else {
    state = state.updateIn(['players', playerId, 'moved'], () => true);
  }

  const location = player.get('location');
  const direction = action.get('direction');
  if (location === null) {
    return [null, null];
  }
  let [new_location, move_err] = moveLocation(state.get('grid'), location, direction);
  if (new_location === null) {
    return [null, move_err];
  }
  let new_player: Player = Map({type: 'player', id: playerId, facing: direction});
  let [push_state, pushError] = tryPush(state, {pushed_items: List([new_player]), new_location, direction});
  if (push_state === null) {
    return [null, pushError];
  }
  // TODO: make sure the player is still in the same location?
  // alternatively, remove the player from the grid before tryPush.  this allows "cyclical" pushing
  // could make a level based on that
  let new_state = setGrid(push_state, location, cellUpdater(items => items.filter(item => getItemType(item) !== 'player' || (item as Player).get('id') !== playerId)));

  return [new_state, null];
  // let items = cell.get('items');
  // let newItems = items.filter((item) => {
  //   return getItemType(item) !== 'player';
  // }).push(player);
  // let newCell = updateCell(cell, (items) => items.filter((item) => {
  //   return getItemType(item) !== 'player';
  // }));
  // let newGrid = updateGrid(grid, location, (cell) => newCell);
  // let newState = state.set('grid', newGrid);
  // return newState;
}

export function allItems(state: GameState): List<Item> {
  return state.get('grid').flatMap((row: List<Cell>) => row.flatMap((cell) => cell.get('items')));
}

export function creatables(state: GameState): List<Item> {
  let creatables: List<Item> = List([]);
  let all_items = allItems(state.get('past').get(0) || state);
  // TODO allow eggs?
  ['player', 'shiny', 'rock'].forEach((t) => {
    if (all_items.find(item => getItemType(item) === t)) {
      creatables = creatables.push(Map({type: t}));
    }
  });
  if (all_items.find(item => getItemType(item) === 'key' || getItemType(item) === 'door')) {
    creatables = creatables.push(Map({type: 'key'}) as Item);
  }
  for (let [wormhole_id, wormhole_state] of state.get('wormholes').entries()) {
    if (wormhole_state.get('outPushable')) {
      let w_out = Map({
        type: 'wormholeOut', id: wormhole_id,
        out_id: 0,  // this will get set later
        pushable: true,
        default_direction: null,
      });
      creatables = creatables.push(w_out);
    }
  }
  return creatables;
}


function tryTriggerTimeEvent(state: GameState, event: Event): [boolean, GameState, Optional<RenderedMessage>] {
  switch (getEventType(event)) {
    case 'exit': {
      let exitevent: ExitEvent = event as ExitEvent;
      let t = exitevent.get('time');
      if (t === null) {
        return [false, state, null];
      }
      // wormhole comparison
      if (getTime(state) >= t) {
        let msg = <React.Fragment>
          {"Expected "} 
          {item_display(state, exitevent.get('item'), {inline: true})}
          {" to enter "}
          {item_display(state, Map({type: 'exit'}), {inline: true})}
        </React.Fragment>
        return [false, state, msg];
      }
      return [false, state, null];
    }
    case 'enter_wormhole': {
      let enter_wormhole_event: EnterWormholeEvent = event as EnterWormholeEvent;
      if (getTime(state) >= enter_wormhole_event.get('time')) {
        let msg = <React.Fragment>
          {item_display(state, enter_wormhole_event.get('item'), {inline: true, })}
          {" "} does not enter the wormhole on time
        </React.Fragment>;
        return [false, state, msg];
      }
      return [false, state, null];
    }
    case 'exit_wormhole': {
      // TODO
      let exit_wormhole_event: ExitWormholeEvent = event as ExitWormholeEvent;
      let wormhole_id = exit_wormhole_event.get('wormhole_id');
      let wormhole_state: WormholeState = state.getIn([ 'wormholes', wormhole_id ]) as WormholeState;
      let past_time = true;
      let matching_out: Optional<WormholeOutState> = null;
      wormhole_state.get('outs').forEach((out_state: WormholeOutState) => {
        let age = wormhole_out_age(state, out_state);
        if (age === null) {
          past_time = false;
        } else if (age <= exit_wormhole_event.get('wormhole_age')) {
          past_time = false;
          if (age === exit_wormhole_event.get('wormhole_age') && out_state.get('location') !== null) {
            if (matching_out !== null) {
              throw new Error('Multiple matching wormhole outs');
            }
            matching_out = out_state;
          }
        }
      })
      if (past_time) {
        throw new Error(`Unexpected:  t=${getTime(state)} already past exit wormhole time ${exit_wormhole_event.get('wormhole_age')}`)
      }
      if (matching_out === null) {
        return [false, state, null];
      }
      let location: Location = (matching_out as WormholeOutState).get('location') as Location;
      let items: List<Item> = exit_wormhole_event.get('items');
      for (let item of items) {
        let itemType = getItemType(item);
        if (itemType === 'wormholeOut') {
          let pushed_out = (item as WormholeOut);
          state = state.setIn(
            ['wormholes', pushed_out.get('id'), 'outs', pushed_out.get('out_id'), 'nested_in'],
            null as any,
          );
          state = state.setIn(
            ['wormholes', pushed_out.get('id'), 'outs', pushed_out.get('out_id'), 'nested_age'],
            null as any,
          );
          let t_diff = getTime(state) - exit_wormhole_event.get('enter_time');
          state = state.updateIn(
            ['wormholes', pushed_out.get('id'), 'outs', pushed_out.get('out_id'), 'delta_t'],
            ((delta_t: number) => { return delta_t + t_diff; }) as any
          );
          state = state.updateIn(
            ['wormholes', pushed_out.get('id'), 'outs', pushed_out.get('out_id'), 'base_t'],
            ((base_t: number) => { return base_t + t_diff; }) as any
          );
        }
      }

      if (!location) {
        return [false, state, <span>Wormhole missing, {" "} {items_display(state, items, {})} could not exit</span>]
      }
      let [maybe_new_state, exit_err] = exitWormhole(state, items, wormhole_id, (matching_out as WormholeOutState).get('out_id'), exit_wormhole_event.get('direction'));
      //
      // let [maybe_new_state, exit_err] = tryPush(
      //   state, {pushed_item: exit_wormhole_event.get('item'), new_location: location, direction: exit_wormhole_event.get('direction')}
      // )
      if (exit_err) {
        return [false, state, exit_err]
      }
      if (maybe_new_state === null) {
        return [false, state, <span>{items_display(state, items, {})}{" "}could not exit wormhole</span>]
      }
      return [true, maybe_new_state, null];
    }
    case 'lay': {
      let layevent: LayEvent = event as LayEvent;
      // let t = layevent.get('time');
      let age = state.getIn(['players', layevent.get('player_id'), 'age']) as number;
      let lay_age = layevent.get('age');
      // if (getTime(state) !== t) {
      if (lay_age !== age) {
        return [false, state, null];
      }

      const location = state.getIn(['players', layevent.get('player_id'), 'location']) as Location | null;
      if (location === null) {
        // this means the player was missing
        // we should avoid wormholes on levels with laying
        return [false, state, `Contact developer!  Player missing during lay event, this shouldn't happen`];
      }
      console.log(location)
      let egg = Map({type: 'egg'});
      let new_state = setGrid(state, location, cellUpdater(items => items.unshift(egg)));
      return [true, new_state, null];
    }
    case 'hatch': {
      let hatchevent: HatchEvent = event as HatchEvent;
      let t = hatchevent.get('time');
      if (getTime(state) !== t) {
        return [false, state, null];
      }

      const egg_loc = findCell(state.get('grid'), (cell) => {
        return cell.get('items').findIndex((i: Item) => (getItemType(i) === 'hatching_egg')) > -1 
      });
      if (egg_loc === null) {
        // this means the egg was missing
        // we should avoid missing eggs with hatch events
        return [false, state, `Contact developer!  Egg missing during hatch event, this shouldn't happen`];
      }
      return [true, state, null];
      // let new_state = setGrid(state, egg_loc, cellUpdater(items => items.filter((item: Item) => (getItemType(item) !== 'egg'))));
      // let eggshell = Map({type: 'eggshell'});
      // let playerId = getNextPlayerId(state);
      // let new_player: Player = Map({type: 'player', id: playerId, facing: null});
      // new_state = setGrid(new_state, egg_loc, cellUpdater(items => items.unshift(eggshell)));
      // new_state = setGrid(new_state, egg_loc, cellUpdater(items => items.unshift(new_player)));
      // new_state = maybeUpdatePushedItemLocation(new_state, new_player, egg_loc);
      // return [true, new_state, null];
    }
    default:
      throw new Error(`Unknown event type ${getEventType(event)}`);
  }
}

export function tryAction(state: GameState, action: Action): Result<Optional<GameState>> {
  let new_state: GameState, err: RenderedMessage | null;
  switch (getActionType(action)) {
    case 'move': {
      let maybe_new_state;
      [maybe_new_state, err] = tryMove(state, action as MoveAction);
      if (err !== null) {
        return [null, err];
      }
      if (maybe_new_state === null) {
        new_state = state; // basically just wait
      } else {
        new_state = maybe_new_state;
      }
      break;
    }
    case 'wait': {
      new_state = state.update('parallel_time', time => time + 1);
      new_state = new_state.update('players', players => players.map((player_s: PlayerState) =>
        player_s.set('moved', false)
      ));
      break;
    }
    case 'enter': {
      let maybe_new_state: Optional<GameState> = state;
      let enter_action: EnterAction = action as EnterAction;
      let dir: Direction | null = enter_action.get('direction');
      let wormhole_id: number = enter_action.get('wormhole_id');
      let wormhole_out_id: number = enter_action.get('wormhole_out_id');
      let item: Item = enter_action.get('item');
      if (getItemType(item) === 'player') {
        item = item.set('facing', dir || enter_action.get('display_direction') || 'up');
        item = item.set('id', getNextPlayerId(state));
      }
      if (getItemType(item) === 'wormholeOut') {
        let pushed_out = item as WormholeOut;
        let new_out_id = (state.getIn(['wormholes', pushed_out.get('id'), 'outs']) as List<WormholeOutState>).size;
        // TODO this assumes we push the latest one in...
        let previous_out_state = state.getIn(['wormholes', pushed_out.get('id'), 'outs', new_out_id - 1]) as WormholeOutState;
        item = item.set('out_id', new_out_id);
        let wh_delta_t = state.getIn(['wormholes', wormhole_id, 'outs', wormhole_out_id, 'delta_t']) as number;
        let out_state: WormholeOutState = Map({
          out_id: new_out_id,
          // TODO figure this all out more properly?
          base_t: getTime(state), // does this need a +1?
          // TODO this should be unknown... we just assume we will resolve based on previous age
          start_age: (previous_out_state.get('start_age') as number) + wh_delta_t,
          delta_t: previous_out_state.get('delta_t') + wh_delta_t,
          location: null, // this will get updated later?
          nested_in: null, // is not nested (that's for positive wormholes)
          nested_age: null,
        })
        maybe_new_state = maybe_new_state.updateIn(['wormholes', pushed_out.get('id'), 'outs'], (outs: any) => outs.push(out_state))
      }
      [maybe_new_state, err] = exitWormhole(maybe_new_state, List([item]), wormhole_id, wormhole_out_id, dir || 'up');
      if (maybe_new_state === null) {
        return [null, err];
      }
      new_state = maybe_new_state;
      let delta_t = new_state.getIn(['wormholes', wormhole_id, 'outs', wormhole_out_id, 'delta_t']) as number;
      new_state = (new_state as any).update('events',
        (events: List<Event>) => events.push(
          Map({
            type: 'enter_wormhole', item: item, wormhole_id: wormhole_id, direction: dir,
            time: getTime(new_state) - delta_t
          })
        )
      );
      new_state = new_state.set('created', true);
      break;
    }
    default:
      return [null, `Unknown action type: ${getActionType(action)}`];
  }

  let triggered_any = true;
  while (triggered_any) {
    triggered_any = false;
    for (let i = 0; i < new_state.get('events').size; i++) {
      let event: Event = new_state.get('events').get(i) as Event;
      let [triggered, maybe_new_state, err] = tryTriggerTimeEvent(new_state, event);
      if (err) {
        return [null, err]
      }
      if (triggered) {
        new_state = maybe_new_state;
        new_state = removeEvent(new_state, event);
        triggered_any = true;
        break;
      }
    }
  }
  new_state = new_state.update('past', (past: List<GameState>) => past.push(state));

  new_state = new_state.update('players', players => players.map(
    (player_s: PlayerState) =>
      player_s.set('age', player_s.get('age') + ((player_s.get('location') === null) ? 0 : 1))
  ));

  // TODO: trigger events for the timestep
  return [new_state, null];
}


